import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config';
import {
  AlertTriangle,
  RefreshCw,
  X,
  MapPin,
  Calendar,
  FileWarning,
  ShieldAlert,
  Lightbulb,
  MessageSquare,
  ShieldCheck,
} from 'lucide-react';
import '../../App.css';

const SEVERITY_COLORS = { low: '#10B981', med: '#F59E0B', high: '#EF4444' };
const SEVERITY_LABELS = { low: 'Low', med: 'Medium', high: 'High' };

/**
 * Violation Translator UI. Used on /violations (full page) and embedded on How Others See You.
 * @param {{ embedded?: boolean }} props — when true, omits page header and uses counterparty empty state when list is empty.
 */
export default function ViolationsTranslatorContent({ embedded = false }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerViolation, setDrawerViolation] = useState(null);

  const fetchViolations = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/violations/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Violations fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load violations.');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchViolations();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      className={
        embedded ? 'violations-page violations-page--embedded' : 'violations-page'
      }
    >
      {!embedded && (
        <header className="violations-header">
          <h1>
            <FileWarning size={28} />
            Violation Translator
          </h1>
          <p className="violations-subtitle">
            Your inspection violations with plain-English explanations and next steps
          </p>
          <button
            type="button"
            className="button secondary"
            onClick={fetchViolations}
            disabled={loading}
          >
            <RefreshCw size={16} /> Refresh
          </button>
        </header>
      )}

      {embedded && (
        <div className="violations-embedded-toolbar">
          <button
            type="button"
            className="button secondary"
            onClick={fetchViolations}
            disabled={loading}
          >
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      )}

      {error && (
        <div className="violations-error" role="alert">
          <AlertTriangle size={20} />
          {error}
        </div>
      )}

      {loading && !list.length ? (
        <div className="violations-loading">
          <RefreshCw size={24} className="spin" />
          <p>Loading violations...</p>
        </div>
      ) : list.length === 0 ? (
        embedded ?
          <div className="violations-empty-positive" role="status">
            <ShieldCheck size={48} strokeWidth={1.75} color="#16A34A" aria-hidden />
            <h3 className="violations-empty-positive__title">No violations on record</h3>
            <p className="violations-empty-positive__body">
              FMCSA inspection violations will appear here when synced. A clean violation history strengthens how
              counterparties evaluate your profile.
            </p>
          </div>
        : <div className="violations-empty">
            <ShieldAlert size={48} />
            <p>No violations on record.</p>
            <p className="violations-empty-hint">
              Violations from FMCSA inspections will appear here once synced.
            </p>
          </div>
      ) : (
        <ul className="violations-list">
          {list.map((v, idx) => (
            <li
              key={`${v.code}-${v.date}-${idx}`}
              className="violations-list-item"
              onClick={() => setDrawerViolation(v)}
            >
              <span
                className="violation-severity-dot"
                style={{ backgroundColor: SEVERITY_COLORS[v.severity] || '#6B7280' }}
                title={SEVERITY_LABELS[v.severity] || v.severity}
              />
              <div className="violation-list-main">
                <span className="violation-code">{v.code}</span>
                <span className="violation-title">{v.title}</span>
              </div>
              <div className="violation-list-meta">
                <span className="violation-date">
                  <Calendar size={14} />
                  {formatDate(v.date)}
                </span>
                {v.location && (
                  <span className="violation-location">
                    <MapPin size={14} />
                    {v.location}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {drawerViolation && (
        <div
          className="violations-drawer-overlay"
          onClick={() => setDrawerViolation(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && setDrawerViolation(null)}
          aria-label="Close drawer"
        />
      )}
      <div
        className={`violations-drawer ${drawerViolation ? 'open' : ''}`}
        aria-hidden={!drawerViolation}
      >
        {drawerViolation && (
          <>
            <div className="violations-drawer-header">
              <div>
                <span
                  className="violation-severity-dot"
                  style={{
                    backgroundColor:
                      SEVERITY_COLORS[drawerViolation.severity] || '#6B7280',
                  }}
                />
                <span className="violation-code">{drawerViolation.code}</span>
              </div>
              <button
                type="button"
                className="violations-drawer-close"
                onClick={() => setDrawerViolation(null)}
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>
            <h2 className="violations-drawer-title">{drawerViolation.title}</h2>
            <div className="violations-drawer-meta">
              <span>
                <Calendar size={16} /> {formatDate(drawerViolation.date)}
              </span>
              {drawerViolation.location && (
                <span>
                  <MapPin size={16} /> {drawerViolation.location}
                </span>
              )}
            </div>

            <section className="violations-drawer-section">
              <h3>
                <MessageSquare size={18} /> Plain English
              </h3>
              <p>{drawerViolation.plainEnglish || 'No description available.'}</p>
            </section>

            {drawerViolation.impact && (
              <section className="violations-drawer-section">
                <h3>
                  <ShieldAlert size={18} /> Impact
                </h3>
                <p>{drawerViolation.impact}</p>
              </section>
            )}

            {drawerViolation.preventionSteps?.length > 0 && (
              <section className="violations-drawer-section">
                <h3>
                  <Lightbulb size={18} /> Prevention steps
                </h3>
                <ul>
                  {drawerViolation.preventionSteps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ul>
              </section>
            )}

            {drawerViolation.disputeTips?.length > 0 && (
              <section className="violations-drawer-section">
                <h3>
                  <MessageSquare size={18} /> Dispute tips
                </h3>
                <ul>
                  {drawerViolation.disputeTips.map((tip, i) => (
                    <li key={i}>{tip}</li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
